import * as React from "react";

import { Spinner } from "../display/Spinner";
import "./button.css";

/**
 * Our button, replacing `@salt-ds/core`'s for the 864 elements this app renders — the
 * highest-traffic control in the product.
 *
 * ── THE CLASSES ARE A CONTRACT, AND ONE OF THEM IS LOAD-BEARING TWICE ─────────────────
 *
 * `src/components/ui/button.tsx` exports `buttonVariants()`, which hands a CLASS STRING to
 * `react-day-picker` for the calendar's day cells — markup this component never renders. So
 * the class names are not merely selected on by tests; they are the entire styling mechanism
 * for another library's DOM. `button.test.tsx` asserts that string equals what rendering
 * actually produces, which is why `saltButton-primary` (the vendor's default `variant`, never
 * overridden by product code) still appears on every button.
 *
 * The split matters: `.saltButton` carries every paint declaration, while the appearance and
 * sentiment classes set ONLY `--button-*` custom properties, which nothing reads without
 * `.saltButton`. Emitting the modifiers alone typechecks, reads as correct, and paints nothing.
 *
 * ── DEFAULTS READ FROM THE VENDOR'S SOURCE, NOT INFERRED ──────────────────────────────
 *
 *   variant   -> "primary"   always in the class list; product code never sets it
 *   appearance-> "solid"     after the variant mapping
 *   sentiment -> "neutral"   ditto — this is the app's grey default button
 *   type      -> "button"    the VENDOR's default. Our wrapper overrides it to "submit",
 *                            which is why the loading rule below is not academic here.
 *
 * ── THE TWO BEHAVIOURS THAT ARE SAFETY, NOT POLISH ────────────────────────────────────
 *
 * 1. A LOADING SUBMIT BUTTON BECOMES type="button". Without it, the button that is already
 *    mid-submit still submits its form on a second click. Our wrapper defaults `type` to
 *    "submit", so every loading button in this app depends on this line.
 * 2. `onClick` IS NOT ATTACHED while loading or disabled — belt and braces for the same
 *    double-submit, and it also stops a `focusableWhenDisabled` button (which has no
 *    `disabled` attribute) from firing its handler.
 *
 * `focusableWhenDisabled` renders `aria-disabled` INSTEAD of `disabled`, so the control stays
 * reachable by keyboard and can still explain why it is unavailable. A plain `disabled`
 * button is skipped by tab order entirely and announces nothing.
 */
export type ButtonAppearance = "solid" | "bordered" | "transparent";
export type ButtonSentiment = "accented" | "neutral" | "positive" | "negative" | "caution";
export type ButtonVariant = "primary" | "secondary" | "cta";

export const ButtonAppearanceValues: ButtonAppearance[] = ["solid", "bordered", "transparent"];
export const ButtonSentimentValues: ButtonSentiment[] = [
  "accented",
  "neutral",
  "positive",
  "negative",
  "caution",
];
export const ButtonVariantValues: ButtonVariant[] = ["primary", "secondary", "cta"];

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  appearance?: ButtonAppearance;
  sentiment?: ButtonSentiment;
  /** The vendor's legacy axis. Kept because it is always emitted as a class. */
  variant?: ButtonVariant;
  loading?: boolean;
  /** Rendered into a visually-hidden status region while loading. */
  loadingAnnouncement?: string;
  /** Keeps a disabled button in the tab order via `aria-disabled`. */
  focusableWhenDisabled?: boolean;
}

function variantToAppearanceAndSentiment(variant: ButtonVariant): {
  appearance: ButtonAppearance;
  sentiment: ButtonSentiment;
} {
  switch (variant) {
    case "secondary":
      return { appearance: "transparent", sentiment: "neutral" };
    case "cta":
      return { appearance: "solid", sentiment: "accented" };
    default:
      return { appearance: "solid", sentiment: "neutral" };
  }
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    appearance: appearanceProp,
    children,
    className,
    disabled,
    focusableWhenDisabled,
    loading,
    loadingAnnouncement,
    onBlur,
    onClick,
    onKeyDown,
    onKeyUp,
    sentiment: sentimentProp,
    type: typeProp = "button",
    variant = "primary",
    ...rest
  },
  ref,
) {
  /*
   * `active` is a VISUAL state the vendor tracks by hand rather than leaning on `:active`,
   * because `:active` does not fire for a keyboard press. Holding Enter or Space on a focused
   * button has to look pressed too, or the keyboard path silently loses the affordance the
   * pointer path has.
   */
  const [active, setActive] = React.useState(false);
  const [keyIsDown, setKeyIsDown] = React.useState("");

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (keyIsDown !== "Enter" && keyIsDown !== " ") setActive(false);
    }, 0);
    return () => clearTimeout(timer);
  }, [active, keyIsDown]);

  const mapped = variantToAppearanceAndSentiment(variant);
  const appearance = appearanceProp ?? mapped.appearance;
  const sentiment = sentimentProp ?? mapped.sentiment;

  // See note 1 above. This is the double-submit guard.
  const type = typeProp === "submit" && loading ? "button" : typeProp;

  const classes = ["saltButton", `saltButton-${variant}`];
  if (loading) classes.push("saltButton-loading");
  if (disabled) classes.push("saltButton-disabled");
  if (active) classes.push("saltButton-active");
  classes.push(`saltButton-${appearance}`, `saltButton-${sentiment}`);
  if (className) classes.push(className);

  return (
    <button
      aria-disabled={disabled && focusableWhenDisabled ? true : undefined}
      className={classes.join(" ")}
      data-loading={loading}
      disabled={disabled && !focusableWhenDisabled}
      onBlur={(event) => {
        setActive(false);
        onBlur?.(event);
      }}
      onClick={loading || disabled ? undefined : (event) => {
        setActive(true);
        onClick?.(event);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          setKeyIsDown(event.key);
          setActive(true);
        }
        onKeyDown?.(event);
      }}
      onKeyUp={(event) => {
        setKeyIsDown("");
        setActive(false);
        onKeyUp?.(event);
      }}
      {...rest}
      ref={ref}
      type={type}
    >
      {loading ? (
        <div aria-hidden className="saltButton-spinner">
          <Spinner aria-hidden disableAnnouncer size="small" />
        </div>
      ) : null}
      {typeof loadingAnnouncement === "string" ? (
        <span className="saltButton-sr-only" role="status">
          {loadingAnnouncement}
        </span>
      ) : null}
      {children}
    </button>
  );
});
